import type {
  CanonicalReportProduct,
  CanonicalReportSourceSummary,
} from './types'

export type ReportSourceKind =
  | 'official'
  | 'retail'
  | 'review'
  | 'social'
  | 'community'
  | 'other'

export interface ReportSourceLink {
  url: string
  label: string
  domain: string
  productNames: string[]
  kind: ReportSourceKind
}

export type ReportSourceProduct = Pick<
  CanonicalReportProduct,
  'name' | 'sources' | 'socialEvidence'
>

const RETAIL_DOMAIN_HINTS = [
  'amazon.',
  'bestbuy.',
  'bloomingdales.',
  'costco.',
  'ebay.',
  'etsy.',
  'macys.',
  'nordstrom.',
  'saksfifthavenue.',
  'sephora.',
  'target.',
  'ulta.',
  'walmart.',
  'wayfair.',
]

function normalizeUrlKey(url: string): string {
  try {
    const parsed = new URL(url)
    parsed.hash = ''
    return parsed.toString().replace(/\/$/, '').toLowerCase()
  } catch {
    return url.trim().toLowerCase()
  }
}

export function getReportSourceDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

function inferSourceKind(
  source: CanonicalReportSourceSummary
): ReportSourceKind {
  const url = source.url.toLowerCase()
  const label = source.label?.toLowerCase() ?? ''
  const platform = source.platform?.toLowerCase() ?? ''

  if (platform === 'reddit' || url.includes('reddit.com')) return 'community'

  if (
    platform === 'youtube' ||
    platform === 'tiktok' ||
    platform === 'instagram' ||
    url.includes('youtube.com') ||
    url.includes('youtu.be') ||
    url.includes('tiktok.com') ||
    url.includes('instagram.com')
  ) {
    return 'social'
  }

  if (
    label.includes('official') ||
    label.includes('manufacturer') ||
    label.includes('brand page') ||
    label.includes('product page')
  ) {
    return 'official'
  }

  if (
    label.includes('review') ||
    label.includes('article') ||
    label.includes('editorial') ||
    label.includes('blog') ||
    label.includes('comparison')
  ) {
    return 'review'
  }

  if (
    label.includes('retailer') ||
    label.includes('price') ||
    label.includes('listing') ||
    label.includes('store') ||
    RETAIL_DOMAIN_HINTS.some((hint) => url.includes(hint))
  ) {
    return 'retail'
  }

  return 'other'
}

function getSourceLabel(source: CanonicalReportSourceSummary): string {
  const label = source.label?.trim()
  if (label) return label
  return getReportSourceDomain(source.url)
}

function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function addSource(
  linksByUrl: Map<string, ReportSourceLink>,
  source: CanonicalReportSourceSummary,
  productName: string
) {
  const url = source.url.trim()
  // Source URLs originate from scraped/LLM-written report data and can be
  // malformed (e.g. spaces in the hostname). Downstream consumers validate
  // hrefs strictly — hub generation inputs reject the whole payload on one
  // bad URL — so unusable links are dropped here.
  if (!url || !isValidHttpUrl(url)) return

  const key = normalizeUrlKey(url)
  const existing = linksByUrl.get(key)
  if (existing) {
    if (!existing.productNames.includes(productName)) {
      existing.productNames.push(productName)
    }
    return
  }

  linksByUrl.set(key, {
    url,
    label: getSourceLabel(source),
    domain: getReportSourceDomain(url),
    productNames: [productName],
    kind: inferSourceKind(source),
  })
}

function getSourceKindRank(kind: ReportSourceKind): number {
  if (kind === 'retail') return Number.MAX_SAFE_INTEGER
  return DISPLAY_SOURCE_KIND_ORDER.indexOf(kind)
}

export function collectReportSources(
  products: ReportSourceProduct[]
): ReportSourceLink[] {
  const linksByUrl = new Map<string, ReportSourceLink>()

  for (const product of products) {
    for (const source of product.sources ?? []) {
      addSource(linksByUrl, source, product.name)
    }

    for (const video of product.socialEvidence?.videos ?? []) {
      addSource(linksByUrl, video, product.name)
    }

    for (const thread of product.socialEvidence?.redditThreads ?? []) {
      addSource(
        linksByUrl,
        {
          url: thread.url,
          label: thread.title ?? 'Reddit thread',
          platform: 'reddit',
        },
        product.name
      )
    }
  }

  return Array.from(linksByUrl.values()).sort((a, b) => {
    const kindDelta = getSourceKindRank(a.kind) - getSourceKindRank(b.kind)
    if (kindDelta !== 0) return kindDelta
    return a.label.localeCompare(b.label)
  })
}

export const DISPLAY_SOURCE_KIND_ORDER: Array<
  Exclude<ReportSourceKind, 'retail'>
> = ['official', 'review', 'social', 'community', 'other']

export const DRAWER_SOURCE_KIND_ORDER: ReportSourceKind[] = [
  'official',
  'review',
  'social',
  'community',
  'retail',
  'other',
]

export const SOURCE_KIND_LABELS: Record<ReportSourceKind, string> = {
  official: 'Official pages',
  retail: 'Retail pages',
  review: 'Reviews and articles',
  social: 'Videos and social',
  community: 'Community threads',
  other: 'Other research',
}
